interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsent {
  timestamp: string;
  version: string;
}

const COOKIE_CONSENT_KEY = "eventToBee_cookieConsent";
const COOKIE_PREFERENCES_KEY = "eventToBee_cookiePreferences";

class CookieManager {
  private preferences: CookiePreferences | null = null;

  constructor() {
    this.loadPreferences();
  }

  private loadPreferences(): void {
    try {
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        this.preferences = JSON.parse(savedPreferences);
      }
    } catch (error) {
      console.error("Error loading cookie preferences:", error);
    }
  }

  public getPreferences(): CookiePreferences | null {
    return this.preferences;
  }

  public hasConsent(): boolean {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    return consent !== null;
  }

  public getConsentInfo(): CookieConsent | null {
    try {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      return consent ? JSON.parse(consent) : null;
    } catch (error) {
      console.error("Error parsing consent info:", error);
      return null;
    }
  }

  public canUseNecessaryCookies(): boolean {
    // Les cookies nécessaires sont toujours autorisés
    return true;
  }

  public canUseFunctionalCookies(): boolean {
    return this.preferences?.functional ?? false;
  }

  public canUseAnalyticsCookies(): boolean {
    return this.preferences?.analytics ?? false;
  }

  public canUseMarketingCookies(): boolean {
    return this.preferences?.marketing ?? false;
  }

  // Méthodes pour gérer spécifiquement chaque type de cookie

  public setThemePreference(theme: "light" | "dark"): void {
    if (this.canUseFunctionalCookies()) {
      localStorage.setItem("theme", theme);
    } else {
      // Utiliser sessionStorage si les cookies fonctionnels ne sont pas autorisés
      sessionStorage.setItem("theme_session", theme);
    }
  }

  public getThemePreference(): string | null {
    if (this.canUseFunctionalCookies()) {
      return localStorage.getItem("theme");
    }
    return sessionStorage.getItem("theme_session");
  }

  public initializeAnalytics(): void {
    if (this.canUseAnalyticsCookies()) {
      // Initialiser Google Analytics ou autres outils d'analyse
      console.log("Analytics cookies enabled - initializing tracking");
      // Exemple: gtag('config', 'GA_MEASUREMENT_ID');
    } else {
      console.log("Analytics cookies disabled - skipping initialization");
    }
  }

  public initializeMarketing(): void {
    if (this.canUseMarketingCookies()) {
      // Initialiser les outils marketing (Facebook Pixel, etc.)
      console.log("Marketing cookies enabled - initializing marketing tools");
    } else {
      console.log("Marketing cookies disabled - skipping marketing tools");
    }
  }

  public cleanupDisabledCookies(): void {
    const preferences = this.getPreferences();
    if (!preferences) return;

    // Nettoyer les cookies selon les préférences
    if (!preferences.functional) {
      // Supprimer les cookies fonctionnels non essentiels
      const functionalCookies = ["theme", "language", "ui_preferences"];
      functionalCookies.forEach((cookie) => {
        localStorage.removeItem(cookie);
        sessionStorage.removeItem(cookie);
      });
    }

    if (!preferences.analytics) {
      // Supprimer les cookies d'analytics
      const analyticsCookies = ["_ga", "_gat", "_gid", "_gtag"];
      analyticsCookies.forEach((cookie) => {
        this.deleteCookie(cookie);
      });
    }

    if (!preferences.marketing) {
      // Supprimer les cookies marketing
      const marketingCookies = ["_fbp", "_fbc", "stripe_mid"];
      marketingCookies.forEach((cookie) => {
        this.deleteCookie(cookie);
      });
    }
  }

  private deleteCookie(name: string): void {
    // Supprimer le cookie en définissant une date d'expiration passée
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
  }

  // Méthode pour recharger les préférences (utile après mise à jour)
  public refreshPreferences(): void {
    this.loadPreferences();
    this.cleanupDisabledCookies();
  }

  // Méthode pour obtenir un résumé des cookies utilisés
  public getCookieSummary(): {
    necessary: string[];
    functional: string[];
    analytics: string[];
    marketing: string[];
  } {
    return {
      necessary: [
        "app_session (SessionStorage) - Token d'authentification sécurisé",
        "token (localStorage) - Token d'authentification legacy",
      ],
      functional: [
        "theme (localStorage) - Préférences de thème sombre/clair",
        "language (localStorage) - Préférences linguistiques",
        "ui_preferences (localStorage) - Paramètres d'interface",
      ],
      analytics: ["_ga - Google Analytics", "_gid - Google Analytics", "_gtag - Google Tag Manager"],
      marketing: ["stripe_mid - Stripe Marketing", "_fbp - Facebook Pixel", "_fbc - Facebook Conversions"],
    };
  }
}

// Instance singleton
export const cookieManager = new CookieManager();

// Fonctions utilitaires pour une utilisation facile
export const canUseAnalytics = () => cookieManager.canUseAnalyticsCookies();
export const canUseMarketing = () => cookieManager.canUseMarketingCookies();
export const canUseFunctional = () => cookieManager.canUseFunctionalCookies();
export const hasUserConsent = () => cookieManager.hasConsent();

// Hook pour React (optionnel)
export const useCookiePreferences = () => {
  const preferences = cookieManager.getPreferences();
  const hasConsent = cookieManager.hasConsent();

  return {
    preferences,
    hasConsent,
    canUseAnalytics: canUseAnalytics(),
    canUseMarketing: canUseMarketing(),
    canUseFunctional: canUseFunctional(),
    refreshPreferences: () => cookieManager.refreshPreferences(),
  };
};
